import { useEffect, useRef } from 'react';
import { pushNotification } from '@/lib/notifications';
import type { FinanceAlert } from '@/lib/finance';

// ─────────────────────────────────────────────────────────────
// Surveillance des alertes financières.
// Quand une alerte ACTIVÉE dans les paramètres apparaît, on la pousse
// dans la cloche ET en notification PWA (une seule fois par alerte).
// ─────────────────────────────────────────────────────────────

const SEEN_KEY = 'zifek-finance-alerts-seen';
const MAX_SEEN = 120;

function alertSignature(alert: FinanceAlert): string {
  return `${alert.key}::${alert.message}`;
}

function readSeen(): string[] {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function writeSeen(list: string[]): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-MAX_SEEN)));
  } catch {
    /* stockage indisponible : on ignore */
  }
}

/**
 * @param merchantId destinataire (le marchand)
 * @param alerts     alertes courantes calculées depuis les vraies données
 * @param ready      false tant que les données ne sont pas chargées
 */
export function useFinanceAlertWatch(
  merchantId: number | null,
  alerts: FinanceAlert[],
  ready: boolean,
): void {
  const signature = alerts.map(alertSignature).join('|');
  const lastSignatureRef = useRef('');

  useEffect(() => {
    if (!ready || !merchantId || !signature) return;
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;

    const seen = readSeen();
    const fresh = alerts.filter((a) => !seen.includes(alertSignature(a))).slice(0, 3);
    if (fresh.length === 0) return;

    writeSeen([...seen, ...fresh.map(alertSignature)]);

    fresh.forEach((alert) => {
      const prefix = alert.severity === 'danger' ? '⚠️ ' : '';
      void pushNotification({
        userId: merchantId,
        type: 'finance',
        message: `${prefix}${alert.message}`,
      });
    });
    // `alerts` est dérivé de `signature` : on ne dépend que de la signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, ready, merchantId]);
}